/**
 * ContentObjectDirective class is responsible for handling the linking of content objects within a directive.
 * It manages the compilation, visibility, and manipulation of content objects based on various conditions and configurations.
 */
export class ContentObjectDirective {
    /**
     * Constructor for ContentObjectDirective.
     * @param {Object} $compile - AngularJS compile service.
     * @param {Object} TaskDataService - Service for handling task data.
     * @param {Object} $signals - Service for emitting signals.
     * @param {Object} $config - Configuration service.
     * @param {Object} DeleteObjectHelperService - Service for deleting objects.
     */
    constructor($compile, TaskDataService, $signals, $config, DeleteObjectHelperService) {
        this._logIdentifier = 'ContentObjectDirective';
        this.abc = null;
        this.$compile = $compile;
        this.TaskDataService = TaskDataService;
        this.$signals = $signals;
        this.$config = $config;
        this.DeleteObjectHelperService = DeleteObjectHelperService;
    }

    /**
     * Link function to handle the linking of the directive.
     * @param {Object} scope - AngularJS scope.
     * @param {Object} element - jQuery element.
     * @param {Object} iAttrs - Attributes of the element.
     */
    link(scope, element, iAttrs) {
        let string = 'ASD';
        let codeUnits = new Uint16Array(string.length);
        for (let i = 0; i < codeUnits.length; i++) {
            codeUnits[i] = string.charCodeAt(i);
        }

        scope.isCMS = isCMS;
        scope.loadObjectUrl = null;
        scope.isPDFMode = this.$config.isPDFMode();

        if (typeof scope.objectRef === 'object') {
            return false;
        }

        // Init
        let taskContext = this.TaskDataService.fetchContext(),
            objectRef = scope.objectRef,
            objectRefObj = taskContext.read(objectRef);

        let container = taskContext.read(scope.rows[scope.rowKey].cols[scope.colKey].list[scope.containerKey].content);
        scope.disabledIncludedInPDF = false;
        if (container.getQuestionPart() === null) {
            scope.disabledIncludedInPDF = true;
        }

        // Validate
        if (!angular.isDefined(objectRef)) {
            console.warn("No objectRef found for ContentObjectDirective, skipping", scope);
            return;
        }

        if (!angular.isDefined(objectRefObj)) {
            console.warn("No objectRefObj found for ContentObjectDirective, skipping", scope);
            return;
        }

        scope.contentObject = objectRefObj;
        scope.isVisible = true;

        if (scope.contentObject.getPreferences().targetHintObject) {
            let targetHintObjectUUID = scope.contentObject.getPreferences().targetHintObject;
            let targetHintObject = taskContext.read(targetHintObjectUUID);

            scope.$watch(
                function () {
                    return targetHintObject.getPreferences().showHint;
                },
                function (newValue, oldValue) {
                    if (newValue) {
                        scope.isVisible = true;
                    } else {
                        scope.isVisible = false;
                    }
                }
            );
        }

        if (!angular.isDefined(scope.contentObject) || scope.contentObject === null) {
            console.error("Content Object references to uuid '" + objectRef.content + "' but no entity ContentObject found with that UUID", scope);
            return;
        }

        // Build directive name
        let directivePrefix = "asf-",
            directiveSuffix = "-object",
            objectType = scope.contentObject.data.objectType,
            objectTypeUcFirst = objectType.charAt(0).toUpperCase() + objectType.slice(1),
            directiveName = directivePrefix + objectTypeUcFirst + directiveSuffix;

        let html;
        if (scope.isCMS && !this.$config.isPreviewMode()) {
            scope.containerObj = taskContext.read(scope.rows[scope.rowKey].cols[scope.colKey].list[scope.containerKey].content);

            scope.onDeleteObject = function () {
                let _contentObj = scope.containerObj;
                if (_contentObj.isShowTabs()) {
                    _contentObj = taskContext.read(_contentObj.data.tabs[scope.subContainerKey].content);
                } else if (_contentObj.isShowCarousels()) {
                    _contentObj = taskContext.read(_contentObj.data.carousels[scope.subContainerKey].content);
                } else if (_contentObj.isShowIaaContainer()) {
                    _contentObj = taskContext.read(_contentObj.data.iaaContainer[scope.subContainerKey].content);
                }

                this.DeleteObjectHelperService.deleteObject(_contentObj, objectRef);
                this.$signals.emit("rendering_rows", scope.rows);
            }.bind(this);

            scope.moveObject_up = function () {
                let _contentObj = scope.containerObj;
                if (_contentObj.isShowTabs()) {
                    _contentObj = taskContext.read(_contentObj.data.tabs[scope.subContainerKey].content);
                } else if (_contentObj.isShowCarousels()) {
                    _contentObj = taskContext.read(_contentObj.data.carousels[scope.subContainerKey].content);
                } else if (_contentObj.isShowIaaContainer()) {
                    _contentObj = taskContext.read(_contentObj.data.iaaContainer[scope.subContainerKey].content);
                }

                _contentObj.flagAsDirty();
                let cancelForeach = false;
                angular.forEach(_contentObj.data.objects, function (objectData, objectDataKey) {
                    if (cancelForeach) {
                        return true;
                    }

                    if (objectRef === objectData && !cancelForeach) {
                        cancelForeach = true;
                        let objectRefs = _contentObj.data.objects;
                        let new_index;
                        if (objectDataKey > 0) {
                            new_index = Object.keys(objectRefs)[objectDataKey - 1];
                        } else {
                            new_index = Object.keys(objectRefs)[objectRefs.length - 1];
                        }

                        let tmpKey = Object.keys(objectRefs)[objectDataKey];
                        let tmp = angular.copy(objectRefs[tmpKey]);

                        objectRefs.splice(tmpKey, 1);
                        objectRefs.splice(new_index, 0, tmp);
                        this.$signals.emit("rendering_rows", scope.rows);
                        this.$signals.emit("scroll_to_element", ".obj-" + scope.rowKey + "-" + scope.colKey + "-" + scope.containerKey + "-" + new_index);

                        return true;
                    }
                }.bind(this));
            }.bind(this);

            scope.moveObject_down = function () {
                let _contentObj = scope.containerObj;
                if (_contentObj.isShowTabs()) {
                    _contentObj = taskContext.read(_contentObj.data.tabs[scope.subContainerKey].content);
                } else if (_contentObj.isShowCarousels()) {
                    _contentObj = taskContext.read(_contentObj.data.carousels[scope.subContainerKey].content);
                } else if (_contentObj.isShowIaaContainer()) {
                    _contentObj = taskContext.read(_contentObj.data.iaaContainer[scope.subContainerKey].content);
                }

                _contentObj.flagAsDirty();
                let cancelForeach = false;
                angular.forEach(_contentObj.data.objects, function (objectData, objectDataKey) {
                    if (cancelForeach) {
                        return false;
                    }

                    if (objectRef === objectData && !cancelForeach) {
                        cancelForeach = true;
                        let objectRefs = _contentObj.data.objects;
                        let new_index;
                        if (objectDataKey < objectRefs.length - 1) {
                            new_index = Object.keys(objectRefs)[objectDataKey + 1]
                        } else {
                            new_index = Object.keys(objectRefs)[0]
                        }

                        let tmpKey = Object.keys(objectRefs)[objectDataKey];
                        let tmp = angular.copy(objectRefs[tmpKey]);

                        objectRefs.splice(tmpKey, 1);
                        objectRefs.splice(new_index, 0, tmp);
                        this.$signals.emit("rendering_rows", scope.rows);
                        this.$signals.emit("scroll_to_element", ".obj-" + scope.rowKey + "-" + scope.colKey + "-" + scope.containerKey + "-" + new_index);

                        return false;
                    }
                }.bind(this));
            }.bind(this);

            let headerHtml = "<div class=\"object-header\">" +
                "<span class=\"object-element-legend\"><!-- object-element-" + scope.objindex + " -->" +
                "<span class=\"trash glyphicon glyphicon-trash\" ng-click=\"onDeleteObject()\" tooltip-placement=\"top\" tooltip=\"{{ 'CMS.LAYOUT-MANAGER.TOOLTIP.OBJECT.TRASH' | translate }}\" tooltip-trigger=\"mouseenter\" tooltip-append-to-body=\"true\"></span>" +
                "<span class=\"arrow glyphicon glyphicon-circle-arrow-up\" ng-click=\"moveObject_up()\" tooltip-placement=\"top\" tooltip=\"{{ 'CMS.LAYOUT-MANAGER.TOOLTIP.OBJECT.ARROW-UP' | translate }}\" tooltip-trigger=\"mouseenter\" tooltip-append-to-body=\"true\"></span>" +
                "<span class=\"arrow glyphicon glyphicon-circle-arrow-down\" ng-click=\"moveObject_down()\" tooltip-placement=\"top\" tooltip=\"{{ 'CMS.LAYOUT-MANAGER.TOOLTIP.OBJECT.ARROW-DOWN' | translate }}\" tooltip-trigger=\"mouseenter\" tooltip-append-to-body=\"true\"></span>" +
                "<span class=\"object-handle handle glyphicon glyphicon-move\" tooltip-placement=\"top\" tooltip=\"{{ 'CMS.LAYOUT-MANAGER.TOOLTIP.OBJECT.MOVE' | translate }}\" tooltip-trigger=\"mouseenter\" tooltip-append-to-body=\"true\"></span>" +
                "</span>" +
                "</div>";
            html = headerHtml + "<" + directiveName + " class=\"object-content\" content-object='contentObject' container-object='containerObj' sub-container-key='subContainerKey' question-part='questionPart'/>";
        } else {
            let allowedObj = [
                'text',
                'video',
                'audio'
            ];

            let readingTimeHtml = "";

            if (allowedObj.indexOf(scope.contentObject.getObjectType()) === -1 && !(scope.contentObject.getPreferences() && scope.contentObject.getPreferences().interactInReadingTime)) {
                readingTimeHtml = "<div class='reading-time' ng-if='isReadingTime'><div><span class='label default' translate='MODULES.SHEET.READING-TIME.OVERLAY.LABEL'></span></div></div>";
            }

            html = readingTimeHtml + "<" + directiveName + " class=\"object-content\" content-object='::contentObject' container-object='::containerObj' sub-container-key='::subContainerKey' ng-show='this.isVisible' />";
        }

        let unwatch = null;
        let compiled = null;
        scope.loadObjectUrl = false;
        let compile = function () {
            if (compiled === true) {
                return;
            }

            element.html(html);

            scope.element = element;

            this.$compile(element.contents())(scope);
        }.bind(this);

        let notPreRendering = [
            'video',
            'audio',
            'iframe'
        ];
        if ((scope.isCMS && !this.$config.isPreviewMode()) || scope.isPDFMode || notPreRendering.indexOf(scope.contentObject.getObjectType()) === -1) {
            scope.loadObjectUrl = true;
            compile();
        } else {
            compile();
            if (scope.tab && !scope.tab.active) {
                unwatch = scope.$watch('tab.active', function (active) {
                    if (compiled && scope.loadObjectUrl) {
                        return;
                    }
                    if (active === true) {
                        scope.loadObjectUrl = true;
                        compile();
                        compiled = true;
                        unwatch();
                    }
                });
            } else {
                scope.loadObjectUrl = true;
                compile()
            }
        }
    }
}

export default function registerContentObjectDirective(module) {
    module.directive('asfContentObject', ['$compile', 'TaskDataService', '$signals', '$config', 'DeleteObjectHelperService',
        function($compile, TaskDataService, $signals, $config, DeleteObjectHelperService) {
            return {
                restrict: 'EA',
                replace: true,
                scope: {
                    objectRef: "=",
                    questionPart: "=",
                    objindex: "=",
                    rowKey: '=',
                    colKey: '=',
                    containerKey: '=',
                    subContainerKey: '=',
                    rows: '=',
                    size: '=',
                    isReadingTime: '=',
                    isVisible: '=?',
                    tab: '='
                },
                controller: ContentObjectDirective,
                controllerAs: 'vm',
                //bindToController: true,
                link: function(scope, element, attrs) {
                    let ctrl = scope.vm;
                    ctrl.link(scope, element, attrs);
                }
            };
        }
    ]);
}